import { CloseCircleOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Checkbox,
  Col,
  ConfigProvider,
  DatePicker,
  Modal,
  Row,
  Select,
  Table,
  TimePicker,
  Typography,
  notification,
} from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useEffect, useState } from "react";
import useApi from "../../utils/useApi";

dayjs.extend(utc);

const { Text } = Typography;
const { Option } = Select;

// Os dias da semana são tratados como números de 0 a 6 para serem utilizados com mais facilidade no backend
const DAYS_OF_WEEK = [
  "Domingo",
  "Segunda",
  "Terça",
  "Quarta",
  "Quinta",
  "Sexta",
  "Sábado",
];

const mapDayToNumber = {
  Segunda: 0,
  Terça: 1,
  Quarta: 2,
  Quinta: 3,
  Sexta: 4,
  Sábado: 5,
  Domingo: 6,
};

const initializeStandardHours = () => {
  let initial = {};
  DAYS_OF_WEEK.forEach((day) => {
    initial[day] = {
      opening: null,
      closing: null,
      closed: false,
    };
  });
  return initial;
};

export default function OpeningHours() {
  const { postStandardSchedule, postSpecialSchedule, getWeeklySchedule } =
    useApi();

  const [weeklySchedule, setWeeklySchedule] = useState([]);

  const [referenceDate, setReferenceDate] = useState(dayjs());

  const [showStandardModal, setShowStandardModal] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);

  const [standardHours, setStandardHours] = useState(initializeStandardHours);

  // Novo estado para gerenciar o dia que servirá de "fonte" para copiar
  const [dayToCopy, setDayToCopy] = useState(null);
  const [customDate, setCustomDate] = useState(null);
  const [customOpening, setCustomOpening] = useState(null);
  const [customClosing, setCustomClosing] = useState(null);
  const [customClosed, setCustomClosed] = useState(false);

  useEffect(() => {
    fetchWeeklySchedule(referenceDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceDate]);

  // Chamada para o backend que retorna o schedule do dia de hoje e os próximos seis dias
  const fetchWeeklySchedule = async (date) => {
    try {
      const dateToFetch = date || dayjs();
      const dateStr = dateToFetch.format("YYYY-MM-DD");

      const response = await getWeeklySchedule(dateStr);
      const data = response.weekly_schedule || [];

      const mappedData = data.map((item) => {
        const parsedDate = dayjs(item.date);
        const dayOfWeekIndex = parsedDate.day(); // day() => 0=Domingo, 1=Segunda...
        return {
          ...item,
          weekday: DAYS_OF_WEEK[dayOfWeekIndex],
        };
      });

      setWeeklySchedule(mappedData);
    } catch (err) {
      console.error("Erro ao buscar weekly schedule:", err);
      notification.error({
        message: "Erro ao buscar horários",
        description: err.message,
      });
    }
  };

  const handlePreviousWeek = () => {
    setReferenceDate((prevDate) => prevDate.subtract(7, "day"));
  };

  const handleNextWeek = () => {
    setReferenceDate((prevDate) => prevDate.add(7, "day"));
  };

  const handleSaveStandard = async () => {
    // Verificar se o horário de fechamento é maior ou igual ao de abertura
    try {
      for (let day of DAYS_OF_WEEK) {
        const { opening, closing, closed } = standardHours[day];
        if (!closed && opening && closing) {
          const openTime = dayjs(opening, "HH:mm");
          const closeTime = dayjs(closing, "HH:mm");

          if (closeTime.isBefore(openTime)) {
            notification.warning({
              message: "Horário inválido",
              description: `Nos dias referentes à ${day}, o horário de fechamento não pode ser menor que o de abertura.`,
            });
            return;
          }
        }
      }

      for (let day of DAYS_OF_WEEK) {
        const { opening, closing, closed } = standardHours[day];
        const dayNumber = mapDayToNumber[day];
        // Caso a loja feche durante algum dia, seu schedule é 00 => 00
        if (closed) {
          await postStandardSchedule({
            day_of_week: dayNumber,
            open_time: "00:00",
            close_time: "00:00",
          });
        } else if (opening && closing) {
          await postStandardSchedule({
            day_of_week: dayNumber,
            open_time: opening,
            close_time: closing,
          });
        }
      }

      notification.success({
        message: "Horários padrão salvos com sucesso!",
      });

      setShowStandardModal(false);
      await fetchWeeklySchedule(referenceDate);
    } catch (error) {
      console.error(error);
      notification.error({
        message: "Erro ao salvar horários padrão",
        description: error?.message,
      });
    }
  };

  const handleSaveCustom = async () => {
    if (!customDate) {
      notification.warning({ message: "Selecione uma data" });
      return;
    }

    let finalOpening = customOpening;
    let finalClosing = customClosing;

    if (customClosed) {
      finalOpening = "00:00";
      finalClosing = "00:00";
    } else {
      if (!customOpening || !customClosing) {
        notification.warning({ message: "Selecione abertura e fechamento" });
        return;
      }
      const openTime = dayjs(customOpening, "HH:mm");
      const closeTime = dayjs(customClosing, "HH:mm");

      if (closeTime.isBefore(openTime)) {
        notification.warning({
          message: "Horário inválido",
          description:
            "O horário de fechamento não pode ser menor que o de abertura.",
        });
        return;
      }
    }

    try {
      const selectedDate = customDate.format("YYYY-MM-DD");
      const dataToSend = {
        date: selectedDate,
        open_time: finalOpening,
        close_time: finalClosing,
      };

      await postSpecialSchedule(dataToSend);
      notification.success({
        message: "Horário personalizado salvo com sucesso!",
      });

      setShowCustomModal(false);

      setCustomDate(null);
      setCustomOpening(null);
      setCustomClosing(null);
      setCustomClosed(false);

      await fetchWeeklySchedule(referenceDate);
    } catch (error) {
      console.error(error);
      notification.error({
        message: "Erro ao salvar horário personalizado",
        description: error?.message,
      });
    }
  };

  const updateStandardHours = (day, isOpening, timeValue) => {
    setStandardHours((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [isOpening ? "opening" : "closing"]: timeValue,
      },
    }));
  };

  const toggleClosed = (day) => {
    setStandardHours((prev) => {
      const willClose = !prev[day].closed;
      return {
        ...prev,
        [day]: {
          ...prev[day],
          closed: willClose,
          opening: willClose ? "00:00" : null,
          closing: willClose ? "00:00" : null,
        },
      };
    });
  };

  const handleCopyHours = () => {
    if (!dayToCopy) return;
    const dayData = standardHours[dayToCopy];
    setStandardHours((prev) => {
      let newHours = { ...prev };
      DAYS_OF_WEEK.forEach((d) => {
        if (d !== dayToCopy) {
          newHours[d] = {
            opening: dayData.opening,
            closing: dayData.closing,
            closed: dayData.closed,
          };
        }
      });
      return newHours;
    });
    notification.info({
      message: `Horários copiados de ${dayToCopy} para todos os outros dias!`,
    });
  };
  const columns = [
    {
      title: "Data",
      dataIndex: "date",
      key: "date",
      render: (value) => dayjs(value).format("DD/MM/YYYY"),
    },
    {
      title: "Dia da semana",
      dataIndex: "weekday",
      key: "weekday",
    },
    {
      title: "Abertura",
      dataIndex: "open_time",
      key: "open_time",
    },
    {
      title: "Fechamento",
      dataIndex: "close_time",
      key: "close_time",
    },
  ];

  return (
    <ConfigProvider
      theme={{
        components: {
          Card: {
            headerBg: "#d0d6e2",
          },
        },
      }}>
      <div
        style={{
          width: "100%",
          marginTop: 20,
          display: "flex",
          justifyContent: "center",
        }}>
        <div style={{ width: "90%" }}>
          <Card title="Gerenciar o horário de funcionamento da loja">
            <div
              style={{
                marginTop: 20,
                display: "flex",
                alignItems: "center",
                gap: "1rem",
              }}>
              <Text strong>Ver horários a partir de:</Text>
              <DatePicker
                value={referenceDate}
                format="DD/MM/YYYY"
                onChange={(date) => {
                  setReferenceDate(date || dayjs());
                }}
              />

              <Button type="default" onClick={() => setReferenceDate(dayjs())}>
                Hoje
              </Button>
            </div>

            <div
              style={{
                marginTop: 20,
                marginBottom: 20,
                display: "flex",
                gap: "10px",
              }}>
              <Button type="primary" onClick={() => setShowStandardModal(true)}>
                Configurar horários padrão
              </Button>
              <Button type="default" onClick={() => setShowCustomModal(true)}>
                Adicionar horário personalizado
              </Button>
            </div>

            <Table
              columns={columns}
              dataSource={weeklySchedule}
              rowKey="date"
              pagination={false}
            />

            <div
              style={{
                marginTop: 20,
                display: "flex",
                justifyContent: "flex-end",
                gap: 16,
              }}>
              <Button onClick={handlePreviousWeek}>{"<"}</Button>
              <Button onClick={handleNextWeek}>{">"}</Button>
            </div>
          </Card>

          {/* Modal de horários padrão */}
          <Modal
            title={
              <span style={{ fontSize: "20px", fontWeight: "bold" }}>
                Configurar horários padrão
              </span>
            }
            open={showStandardModal}
            onCancel={() => setShowStandardModal(false)}
            onOk={handleSaveStandard}
            okText="Salvar"
            cancelText="Cancelar">
            <Row style={{ marginBottom: 16 }} gutter={32}>
              <Col span={24}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Select
                    placeholder="Copiar horários de..."
                    style={{ width: 180 }}
                    onChange={(value) => setDayToCopy(value)}
                    value={dayToCopy}>
                    {DAYS_OF_WEEK.map((day) => (
                      <Option key={day} value={day}>
                        {day}
                      </Option>
                    ))}
                  </Select>
                  <Button
                    type="default"
                    onClick={handleCopyHours}
                    disabled={!dayToCopy}>
                    Copiar para todos
                  </Button>
                </div>
              </Col>
            </Row>
            <Row style={{ marginBottom: 24, marginTop: 24 }} gutter={32}>
              <Col span={6}>
                <Text strong>Dia da semana</Text>
              </Col>
              <Col span={8}>
                <Text strong>Abertura</Text>
              </Col>
              <Col span={8}>
                <Text strong>Fechamento</Text>
              </Col>
              <Col span={2}></Col>
            </Row>

            {DAYS_OF_WEEK.map((day) => (
              <Row key={day} style={{ marginBottom: 16 }} gutter={32}>
                <Col
                  span={6}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    height: 32,
                  }}>
                  <Text strong>{day}</Text>
                </Col>

                <Col span={8}>
                  <TimePicker
                    style={{ width: "100%" }}
                    value={
                      standardHours[day].opening
                        ? dayjs(standardHours[day].opening, "HH:mm")
                        : null
                    }
                    format="HH:mm"
                    onChange={(time, timeString) =>
                      updateStandardHours(day, true, timeString)
                    }
                    disabled={standardHours[day].closed}
                  />
                </Col>

                <Col span={8}>
                  <TimePicker
                    style={{ width: "100%" }}
                    value={
                      standardHours[day].closing
                        ? dayjs(standardHours[day].closing, "HH:mm")
                        : null
                    }
                    format="HH:mm"
                    onChange={(time, timeString) =>
                      updateStandardHours(day, false, timeString)
                    }
                    disabled={standardHours[day].closed}
                  />
                </Col>

                {(day === "Segunda" || day === "Domingo") && (
                  <Col
                    span={2}
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                    }}>
                    <CloseCircleOutlined
                      style={{
                        fontSize: 20,
                        color: standardHours[day].closed ? "red" : "#999",
                        cursor: "pointer",
                      }}
                      onClick={() => toggleClosed(day)}
                    />
                  </Col>
                )}
              </Row>
            ))}

            <Row style={{ color: "grey", marginTop: 25, marginBottom: 20 }}>
              Clique no X caso a loja não abra nos devidos dias
            </Row>
          </Modal>

          {/* Modal de horários personalizados */}
          <Modal
            title={
              <span style={{ fontSize: "18px", fontWeight: "bold" }}>
                Adicionar horário personalizado
              </span>
            }
            open={showCustomModal}
            onCancel={() => setShowCustomModal(false)}
            onOk={handleSaveCustom}
            okText="Salvar"
            cancelText="Cancelar">
            <div style={{ marginTop: 20, marginBottom: 20 }}>
              <Row style={{ marginBottom: 24 }} gutter={24}>
                <Col span={24}>
                  <Text strong>Selecione a data:</Text>
                  <div style={{ marginTop: 8 }}>
                    <DatePicker
                      format="DD/MM/YYYY"
                      value={customDate}
                      onChange={(date) => setCustomDate(date)}
                    />
                  </div>
                </Col>
              </Row>

              <Row style={{ marginBottom: 24 }} gutter={24}>
                <Col span={12}>
                  <Text strong>Abertura:</Text>
                  <div style={{ marginTop: 8 }}>
                    <TimePicker
                      value={
                        customOpening ? dayjs(customOpening, "HH:mm") : null
                      }
                      format="HH:mm"
                      onChange={(time, timeString) =>
                        setCustomOpening(timeString)
                      }
                      disabled={customClosed}
                    />
                  </div>
                </Col>
                <Col span={12}>
                  <Text strong>Fechamento:</Text>
                  <div style={{ marginTop: 8 }}>
                    <TimePicker
                      value={
                        customClosing ? dayjs(customClosing, "HH:mm") : null
                      }
                      format="HH:mm"
                      onChange={(time, timeString) =>
                        setCustomClosing(timeString)
                      }
                      disabled={customClosed}
                    />
                  </div>
                </Col>
              </Row>

              <Row style={{ marginBottom: 24 }} gutter={24}>
                <Col span={24}>
                  <Checkbox
                    checked={customClosed}
                    onChange={(e) => setCustomClosed(e.target.checked)}>
                    Fechar a loja neste dia (00:00 → 00:00)
                  </Checkbox>
                </Col>
              </Row>
            </div>
          </Modal>
        </div>
      </div>
    </ConfigProvider>
  );
}
